import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '@core/tenant/TenantProvider';

const NAMESPACES_TO_INJECT = ['booking', 'common', 'auth', 'profile', 'gift-cards', 'errors'];

/**
 * Maps legacy appTexts keys to their current dineflow equivalents.
 *
 * When a restaurant has customized text under a legacy key name,
 * this map ensures the value is also injected under the current
 * key name so it takes effect in the UI.
 *
 * Only includes keys where the legacy name cannot be used directly
 * (too generic, contains special characters, or multiple legacy keys
 * map to the same concept).
 */
const LEGACY_ALIASES: Record<string, { key: string; ns: string }> = {
  // Too generic to use as key names
  DATE: { key: 'CONFIRMATION_DATE', ns: 'booking' },
  TIME: { key: 'CONFIRMATION_TIME', ns: 'booking' },

  // Legacy key has a space — cannot be used directly
  'NEW_MODAL_TERMS_RESERVATION DAY': { key: 'NEW_MODAL_TERMS_RESERVATION_DAY', ns: 'booking' },

  // Multiple legacy keys map to same concept
  MODAL_WARNING_WEEKLY_TITLE_1: { key: 'MODAL_WARNING_DAILY_TITLE_1', ns: 'booking' },
  MODAL_WARNING_MONTHLY_TITLE_1: { key: 'MODAL_WARNING_DAILY_TITLE_1', ns: 'booking' },
};

/**
 * Injects restaurant-specific appTexts into i18next resources.
 *
 * appTexts from the backend are flat UPPER_SNAKE_CASE keys per language:
 *   { "en": { "NEW_HOME_BOOK": "Buy Tickets", ... }, "es": { ... } }
 *
 * Since dineflow now uses the same UPPER_SNAKE keys in its JSON files,
 * we inject them directly into the corresponding namespace using
 * i18n.addResourceBundle(). Any key present in appTexts overrides
 * the static default from the JSON file.
 *
 * For legacy keys that were renamed in dineflow, LEGACY_ALIASES maps
 * the old key name to the new one, so restaurant customizations still apply.
 *
 * Renders nothing — this is a side-effect-only component.
 */
export function AppTextsInjector() {
  const { restaurant } = useTenant();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!restaurant?.appTexts) return;

    for (const [lang, texts] of Object.entries(restaurant.appTexts)) {
      if (!texts || typeof texts !== 'object') continue;

      // Build alias overrides: legacy key values mapped to their dineflow equivalents
      const aliasOverrides: Record<string, Record<string, string>> = {};
      for (const [legacyKey, value] of Object.entries(texts as Record<string, string>)) {
        const alias = LEGACY_ALIASES[legacyKey];
        if (alias && typeof value === 'string') {
          if (!aliasOverrides[alias.ns]) aliasOverrides[alias.ns] = {};
          aliasOverrides[alias.ns][alias.key] = value;
        }
      }

      // Inject direct keys (all namespaces get the full texts object — i18next ignores unknown keys)
      for (const ns of NAMESPACES_TO_INJECT) {
        i18n.addResourceBundle(lang, ns, texts, true, true);
      }

      // Inject alias overrides per namespace (after direct, so aliases don't override
      // if the restaurant already has the new key name customized too)
      for (const [ns, overrides] of Object.entries(aliasOverrides)) {
        const merged: Record<string, string> = {};
        for (const [key, value] of Object.entries(overrides)) {
          // Only apply alias if the new key wasn't already customized directly
          if (!(texts as Record<string, string>)[key]) {
            merged[key] = value;
          }
        }
        if (Object.keys(merged).length > 0) {
          i18n.addResourceBundle(lang, ns, merged, true, true);
        }
      }
    }

    if (import.meta.env.DEV) {
      const sampleLang = Object.keys(restaurant.appTexts)[0];
      const keyCount = sampleLang ? Object.keys(restaurant.appTexts[sampleLang]).length : 0;
      console.log(`[AppTextsInjector] Injected ${keyCount} custom keys for ${Object.keys(restaurant.appTexts).length} languages`);
    }
  }, [restaurant?.appTexts, i18n]);

  return null;
}
